import React, { useState, useEffect } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import Avatar from '../../components/ui/Avatar';
import { getMemberWellness, setMemberSharing } from './businessApi';

const MOOD_EMOJI = { great:'😄', good:'🙂', neutral:'😐', low:'😔', bad:'😞' };
const MOOD_COLOR = { great:'#4AC87A', good:'#C8A84B', neutral:'#8899BB', low:'#D4873A', bad:'#C05050' };

const ROLES = {
  owner:   { label:'Owner',   nl:'Eigenaar', color:'#C8A84B' },
  admin:   { label:'Admin',   nl:'Admin',    color:'#9B59B6' },
  manager: { label:'Manager', nl:'Manager',  color:'#5B9ED0' },
  hr:      { label:'HR',      nl:'HR',       color:'#2A9E82' },
  member:  { label:'Member',  nl:'Lid',      color:C.silver  },
};

export default function BusinessMemberProfileScreen({ member, businessId, lang, currentUserId, myRole, onBack, onChat }) {
  const nl        = lang === 'nl';
  const isMe      = member.user_id === currentUserId;
  const canManage = myRole === 'owner' || myRole === 'admin' || myRole === 'manager';

  const [wellness, setWellness]           = useState(null);
  const [loadingW, setLoadingW]           = useState(false);
  const [sharing, setSharing]             = useState(!!member.sharing_enabled);
  const [savingSharing, setSavingSharing] = useState(false);
  const [sharingMsg, setSharingMsg]       = useState('');

  const roleInfo = ROLES[member.role] || ROLES.member;

  useEffect(() => {
    if (!isMe && (canManage || true)) {
      setLoadingW(true);
      getMemberWellness(businessId, member.user_id)
        .then(d => { setWellness(d); setLoadingW(false); })
        .catch(() => setLoadingW(false));
    }
  }, [businessId, member.user_id, isMe, canManage]); // eslint-disable-line

  const toggleSharing = async () => {
    setSavingSharing(true);
    const newVal = !sharing;
    await setMemberSharing(businessId, member.user_id, newVal).catch(() => {});
    setSharing(newVal);
    setSavingSharing(false);
    setSharingMsg(newVal
      ? (nl ? '✓ Welzijnsdata wordt gedeeld met je organisatie' : '✓ Wellness data is now shared with your organization')
      : (nl ? '✓ Welzijnsdata wordt niet meer gedeeld' : '✓ Wellness data sharing disabled'));
    setTimeout(() => setSharingMsg(''), 3000);
  };

  const joinDate = member.joined_at ? new Date(member.joined_at).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { year:'numeric', month:'long', day:'numeric' }) : null;

  return (
    <div style={{ minHeight: '100vh', background: _T.bg, display: 'flex', flexDirection: 'column' }}>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ background: _T.bg2, borderBottom: `1px solid ${_T.cardBorderTop}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: _T.accent, fontSize: 14, cursor: 'pointer', padding: 0 }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 16px 40px' }}>

        {/* Avatar + Name */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ position: 'relative', marginBottom: 14 }}>
            <Avatar user={{ id: member.user_id, name: member.name, color: member.accent_color }} size={88} profileImage={member.picture} />
            <div style={{ position: 'absolute', bottom: -4, right: -4, width: 20, height: 20, borderRadius: '50%', background: '#4AC87A', border: `3px solid ${_T.bg}` }} />
          </div>
          <div style={{ color: C.white, fontSize: 22, fontWeight: 700, fontFamily: 'Georgia,serif', marginBottom: 6 }}>{member.name}</div>
          <span style={{ fontSize: 12, color: roleInfo.color, background: `${roleInfo.color}20`, border: `1px solid ${roleInfo.color}44`, borderRadius: 20, padding: '4px 12px', fontWeight: 700 }}>
            {nl ? (roleInfo.nl || roleInfo.label) : roleInfo.label}
          </span>
          {member.department_name && (
            <div style={{ color: C.silver, fontSize: 12, marginTop: 6 }}>🏢 {member.department_name}</div>
          )}
          {joinDate && (
            <div style={{ color: C.silver2, fontSize: 11, marginTop: 4 }}>
              {nl ? `Lid sinds ${joinDate}` : `Member since ${joinDate}`}
            </div>
          )}
        </div>

        {/* Action buttons */}
        {!isMe && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
            <button onClick={() => onChat && onChat(member)}
              style={{ flex: 1, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 14, fontWeight: 700, padding: '13px 0', cursor: 'pointer' }}>
              💬 {nl ? 'Chat' : 'Chat'}
            </button>
          </div>
        )}

        {/* Own sharing toggle */}
        {isMe && (
          <div style={{ background: _T.bg3, borderRadius: 16, padding: '16px', marginBottom: 20, border: `1px solid ${_T.cardBorderBot}` }}>
            <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 10 }}>
              {nl ? 'WELZIJNSDATA DELEN' : 'WELLNESS DATA SHARING'}
            </div>
            <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.6, marginBottom: 14 }}>
              {nl
                ? 'Deel je slaap, stemming en activiteitsgegevens met je organisatie. Dit helpt het management je beter te ondersteunen.'
                : 'Share your sleep, mood, and activity data with your organization. This helps management support you better.'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: sharing ? '#4AC87A' : C.silver, fontSize: 13, fontWeight: 600 }}>
                {sharing ? (nl ? '✓ Actief gedeeld' : '✓ Sharing active') : (nl ? 'Niet gedeeld' : 'Not sharing')}
              </span>
              <button onClick={toggleSharing} disabled={savingSharing}
                style={{ width: 52, height: 28, borderRadius: 14, background: sharing ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: `1px solid ${sharing ? _T.accent : _T.cardBorderBot}`, cursor: 'pointer', position: 'relative', transition: 'all 0.2s', padding: 0, flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: 4, left: sharing ? 26 : 4, width: 20, height: 20, borderRadius: '50%', background: sharing ? '#0a1220' : C.silver, transition: 'left 0.2s' }} />
              </button>
            </div>
            {sharingMsg && <div style={{ color: '#4AC87A', fontSize: 11, marginTop: 8 }}>{sharingMsg}</div>}
          </div>
        )}

        {/* Wellness stats */}
        {loadingW && (
          <div style={{ textAlign: 'center', color: C.silver, padding: 20, fontSize: 12 }}>...</div>
        )}

        {wellness && !loadingW && (
          <>
            {wellness.sharing_enabled ? (
              <>
                <div style={{ fontSize: 9, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>
                  {nl ? 'WELZIJNSDATA (30 DAGEN)' : 'WELLNESS DATA (30 DAYS)'}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 20 }}>
                  <div style={{ background: _T.bg3, borderRadius: 14, padding: '14px 10px', border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
                    <div style={{ fontSize: 24, marginBottom: 4 }}>😴</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.white }}>
                      {wellness.avg_sleep ? wellness.avg_sleep.toFixed(1) : '—'}
                    </div>
                    <div style={{ fontSize: 9, color: C.silver, marginTop: 2 }}>{nl ? 'Gem. slaap' : 'Avg sleep'}</div>
                  </div>
                  <div style={{ background: _T.bg3, borderRadius: 14, padding: '14px 10px', border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
                    <div style={{ fontSize: 24, marginBottom: 4 }}>
                      {wellness.last_mood ? (MOOD_EMOJI[wellness.last_mood] || '😐') : '😐'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: wellness.last_mood ? (MOOD_COLOR[wellness.last_mood] || C.white) : C.silver }}>
                      {wellness.avg_mood ? wellness.avg_mood.toFixed(1) : '—'}
                    </div>
                    <div style={{ fontSize: 9, color: C.silver, marginTop: 2 }}>{nl ? 'Gem. stemming' : 'Avg mood'}</div>
                  </div>
                  <div style={{ background: _T.bg3, borderRadius: 14, padding: '14px 10px', border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
                    <div style={{ fontSize: 24, marginBottom: 4 }}>
                      {wellness.ex_sessions >= 5 ? '🔥' : wellness.ex_sessions >= 2 ? '💪' : '🌱'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.white }}>{wellness.ex_sessions ?? 0}</div>
                    <div style={{ fontSize: 9, color: C.silver, marginTop: 2 }}>{nl ? 'Sessies' : 'Sessions'}</div>
                  </div>
                </div>

                {/* Wellness quality bar */}
                {wellness.avg_mood !== null && (
                  <div style={{ background: _T.bg3, borderRadius: 14, padding: 16, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 20 }}>
                    <div style={{ color: C.white, fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
                      {nl ? 'Welzijnsscore' : 'Wellness Score'}
                    </div>
                    {(() => {
                      const score = Math.min(100, Math.round(((wellness.avg_mood || 3) / 5 * 50) + ((Math.min(wellness.ex_sessions || 0, 10) / 10) * 30) + ((Math.min(wellness.avg_sleep || 7, 9) / 9) * 20)));
                      const color = score >= 70 ? '#4AC87A' : score >= 45 ? '#C8A84B' : '#C05050';
                      return (
                        <>
                          <div style={{ background: _T.bg4, borderRadius: 10, height: 12, marginBottom: 8, overflow: 'hidden' }}>
                            <div style={{ width: `${score}%`, height: '100%', background: `linear-gradient(90deg,${color},${color}bb)`, borderRadius: 10, transition: 'width 0.8s ease' }} />
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ color }}>{score >= 70 ? (nl ? 'Goed' : 'Good') : score >= 45 ? (nl ? 'Gemiddeld' : 'Average') : (nl ? 'Aandacht nodig' : 'Needs attention')}</span>
                            <span style={{ color, fontWeight: 700 }}>{score}/100</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </>
            ) : (
              <div style={{ background: _T.bg3, borderRadius: 14, padding: 20, border: `1px solid ${_T.cardBorderBot}`, textAlign: 'center' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>🔒</div>
                <div style={{ color: C.silver, fontSize: 13 }}>
                  {nl ? `${isMe ? 'Schakel datadeling in om statistieken te zien' : 'Dit teamlid deelt zijn/haar welzijnsdata nog niet'}` : `${isMe ? 'Enable data sharing to see stats' : 'This member has not enabled wellness data sharing'}`}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
